import React from 'react';

// Brand colors
const CYAN_PRIMARY = 'var(--pm-cyan)';
const CYAN_DIM = 'var(--pm-cyan-glow)';
const CYAN_GLOW = 'var(--pm-cyan-glow)';
const GRAY_DIM = 'var(--pm-gray-dim)';
const GRAY_TEXT = 'var(--pm-gray-text)';
const GRAY_TRACK = 'var(--pm-gray-track)';
const BG_DARK = 'var(--pm-bg-base)';

interface ScriptGenerationAnimationProps {
  completedClips: number;
  totalClips: number;
}

// Small "x" sparkle mark
const SparkleX: React.FC<{ size: number; delay: number; style?: React.CSSProperties }> = ({
  size,
  delay,
  style
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    style={{
      position: 'absolute',
      animation: `sparkle 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      ...style,
    }}
  >
    <path
      d="M2 2L10 10M10 2L2 10"
      stroke={CYAN_PRIMARY}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const ScriptGenerationAnimation: React.FC<ScriptGenerationAnimationProps> = ({
  completedClips,
  totalClips,
}) => {
  // Calculate progress percentage
  const progress = totalClips > 0 ? (completedClips / totalClips) * 100 : 0;

  // SVG circle parameters for progress ring (120px ring)
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes linePulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.8;
          }
        }

        @keyframes dotPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 0.8;
            transform: scale(1) rotate(90deg);
          }
        }

        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 0 20px ${CYAN_DIM}, 0 0 40px rgba(0, 212, 255, 0.1);
          }
          50% {
            box-shadow: 0 0 30px ${CYAN_GLOW}, 0 0 60px rgba(0, 212, 255, 0.2);
          }
        }

        @keyframes flowPulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes iconFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes soundWave {
          0%, 100% {
            opacity: 0.3;
            transform: scaleX(0.8);
          }
          50% {
            opacity: 1;
            transform: scaleX(1);
          }
        }

        @keyframes progressGlow {
          0%, 100% {
            filter: drop-shadow(0 0 6px ${CYAN_DIM});
          }
          50% {
            filter: drop-shadow(0 0 12px ${CYAN_GLOW});
          }
        }
      `}</style>

      {/* Full container matching other status animations */}
      <div
        className="border border-border rounded-lg p-10 flex items-center justify-center"
        style={{ background: 'var(--pm-gradient-dark)' }}
      >
        <div className="text-center">
          <h3 className="text-h1 mb-2">Generating Scripts</h3>
          <p className="text-muted-foreground mb-8">
            Creating voiceover scripts from your articles...
          </p>

          {/* Animation content - vertical layout */}
          <div className="flex flex-col items-center gap-8">
            {/* Article → Flow → Voice Script row */}
            <div className="flex items-center gap-6">
              {/* Left: Article/News Layout Icon */}
              <div className="relative">
                <div
                  className="relative rounded-lg overflow-hidden"
                  style={{
                    width: '90px',
                    height: '110px',
                    backgroundColor: BG_DARK,
                    border: `2px solid ${CYAN_PRIMARY}`,
                    animation: 'glowPulse 3s ease-in-out infinite',
                  }}
                >
                  <div className="p-2">
                    {/* Headline bar at top */}
                    <div
                      className="h-2 rounded-sm mb-2"
                      style={{
                        width: '85%',
                        backgroundColor: GRAY_DIM,
                        animation: 'linePulse 2s ease-in-out infinite',
                      }}
                    />

                    {/* Image placeholder + text lines row */}
                    <div className="flex gap-1.5 mb-2">
                      {/* Image placeholder */}
                      <div
                        className="rounded-sm flex-shrink-0"
                        style={{
                          width: '28px',
                          height: '28px',
                          backgroundColor: GRAY_DIM,
                          opacity: 0.5,
                          animation: 'linePulse 2s ease-in-out infinite',
                          animationDelay: '0.3s',
                        }}
                      />
                      {/* Text lines beside image */}
                      <div className="flex flex-col gap-1 flex-1">
                        <div
                          className="h-1 rounded-full"
                          style={{
                            width: '100%',
                            backgroundColor: GRAY_DIM,
                            animation: 'linePulse 2s ease-in-out infinite',
                            animationDelay: '0.2s',
                          }}
                        />
                        <div
                          className="h-1 rounded-full"
                          style={{
                            width: '80%',
                            backgroundColor: GRAY_DIM,
                            animation: 'linePulse 2s ease-in-out infinite',
                            animationDelay: '0.4s',
                          }}
                        />
                        <div
                          className="h-1 rounded-full"
                          style={{
                            width: '90%',
                            backgroundColor: GRAY_DIM,
                            animation: 'linePulse 2s ease-in-out infinite',
                            animationDelay: '0.6s',
                          }}
                        />
                      </div>
                    </div>

                    {/* Paragraph lines at bottom */}
                    <div className="space-y-1">
                      {[95, 85, 90, 70].map((width, index) => (
                        <div
                          key={index}
                          className="h-1 rounded-full"
                          style={{
                            width: `${width}%`,
                            backgroundColor: GRAY_DIM,
                            animation: 'linePulse 2s ease-in-out infinite',
                            animationDelay: `${0.8 + index * 0.15}s`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Flow Indicator */}
              <div
                className="flex items-center gap-0"
                style={{ animation: 'flowPulse 2s ease-in-out infinite' }}
              >
                {/* Line segment */}
                <div
                  className="h-[2px] w-6"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                {/* First dot */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: CYAN_PRIMARY,
                    animation: 'dotPulse 1.5s ease-in-out infinite',
                  }}
                />
                {/* Line segment */}
                <div
                  className="h-[2px] w-6"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                {/* Second dot */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: CYAN_PRIMARY,
                    animation: 'dotPulse 1.5s ease-in-out infinite',
                    animationDelay: '0.3s',
                  }}
                />
                {/* Arrow */}
                <div
                  className="h-[2px] w-4"
                  style={{ backgroundColor: CYAN_DIM }}
                />
                <div
                  className="w-0 h-0"
                  style={{
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderLeft: `10px solid ${CYAN_PRIMARY}`,
                  }}
                />
              </div>

              {/* Right: Voice Script (Document + Microphone) */}
              <div className="relative">
                <SparkleX size={10} delay={0.5} style={{ top: '-12px', right: '-12px' }} />
                <SparkleX size={8} delay={2.2} style={{ bottom: '-14px', left: '50%', transform: 'translateX(-50%)' }} />

                <div
                  className="relative rounded-lg overflow-hidden"
                  style={{
                    width: '90px',
                    height: '110px',
                    backgroundColor: BG_DARK,
                    border: `2px solid ${CYAN_PRIMARY}`,
                    animation: 'glowPulse 3s ease-in-out infinite',
                    animationDelay: '0.5s',
                  }}
                >
                  {/* Script lines */}
                  <div className="p-2.5 space-y-1.5">
                    {[70, 85, 65, 80, 60].map((width, index) => (
                      <div
                        key={index}
                        className="h-1 rounded-full"
                        style={{
                          width: `${width}%`,
                          backgroundColor: CYAN_PRIMARY,
                          opacity: 0.6,
                          boxShadow: `0 0 4px ${CYAN_DIM}`,
                          animation: 'linePulse 2s ease-in-out infinite',
                          animationDelay: `${index * 0.2}s`,
                        }}
                      />
                    ))}
                  </div>

                  {/* Microphone with sound waves overlay - positioned bottom right */}
                  <div
                    className="absolute flex items-center"
                    style={{
                      bottom: '8px',
                      right: '12px',
                    }}
                  >
                    {/* Microphone icon */}
                    <svg
                      width="18"
                      height="26"
                      viewBox="0 0 20 28"
                      fill="none"
                    >
                      {/* Mic body */}
                      <rect
                        x="5"
                        y="2"
                        width="10"
                        height="16"
                        rx="5"
                        fill={CYAN_PRIMARY}
                        opacity="0.9"
                      />
                      {/* Mic stand arc */}
                      <path
                        d="M3 14 Q3 22 10 22 Q17 22 17 14"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="2"
                        fill="none"
                      />
                      {/* Mic stand */}
                      <line
                        x1="10"
                        y1="22"
                        x2="10"
                        y2="26"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="2"
                      />
                      {/* Base */}
                      <line
                        x1="5"
                        y1="26"
                        x2="15"
                        y2="26"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>

                    {/* Sound waves - to the right of mic */}
                    <svg
                      width="20"
                      height="24"
                      viewBox="0 0 20 24"
                      style={{ marginLeft: '2px' }}
                    >
                      {/* Sound wave arcs */}
                      <path
                        d="M2 7 Q6 12 2 17"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="1.5"
                        fill="none"
                        style={{
                          animation: 'soundWave 2s ease-in-out infinite',
                          transformOrigin: 'left center',
                        }}
                      />
                      <path
                        d="M6 5 Q11 12 6 19"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="1.5"
                        fill="none"
                        style={{
                          animation: 'soundWave 2s ease-in-out infinite',
                          animationDelay: '0.15s',
                          transformOrigin: 'left center',
                        }}
                      />
                      <path
                        d="M10 3 Q17 12 10 21"
                        stroke={CYAN_PRIMARY}
                        strokeWidth="1.5"
                        fill="none"
                        style={{
                          animation: 'soundWave 2s ease-in-out infinite',
                          animationDelay: '0.3s',
                          transformOrigin: 'left center',
                        }}
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="flex flex-col items-center gap-3">
              {/* Circular Progress Ring */}
              <div
                className="relative"
                style={{
                  width: '120px',
                  height: '120px',
                  animation: 'progressGlow 2s ease-in-out infinite',
                }}
              >
                <svg width="120" height="120" className="transform -rotate-90">
                  {/* Background track */}
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={GRAY_TRACK}
                    strokeWidth="6"
                  />
                  {/* Progress stroke */}
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={CYAN_PRIMARY}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                      transition: 'stroke-dashoffset 0.5s ease-out',
                    }}
                  />
                </svg>

                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-3xl font-bold"
                    style={{ color: CYAN_PRIMARY }}
                  >
                    {completedClips}
                  </span>
                  <span className="text-sm" style={{ color: GRAY_TEXT }}>
                    of {totalClips}
                  </span>
                </div>
              </div>

              {/* Label below ring */}
              <span className="text-base" style={{ color: GRAY_TEXT }}>
                scripts ready
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScriptGenerationAnimation;
